import type { DependencyGraphManager } from '../manager/DependencyGraphManager';
import type { EntityId } from '../manager/types';
import type { DependencyUpdate } from './types';

type SubscriptionCallbacks = Map<string, () => void>;

interface SubscriptionParams {
  id: EntityId;
  callback: () => void;
  key: string;
}

interface UnsubscribeParams {
  id: EntityId;
  key: string;
}

const processCallbacks = (callbacks: SubscriptionCallbacks): void => {
  const entries = Array.from(callbacks.values());
  for (let i = 0; i < entries.length; i++) {
    entries[i]();
  }
};

export class DependencyGraphSubscriptions {
  private subscriptions = new Map<EntityId, SubscriptionCallbacks>();
  private graphManager: DependencyGraphManager | null;
  private notifyingUpdateForId: EntityId | null = null;

  // Ids with a flush already scheduled this tick. Dedupe target: the ancestor fan-out
  // (buildInstanceContext) notifies a hot Repeatable ancestor once per descendant write,
  // but its subscribers only need to re-read once. We keep ONE timer per unique id
  // (work stays spread across tasks → no single long task / freeze), collapsing the
  // duplicates.
  private pendingNotifyIds = new Set<EntityId>();

  /**
   * Create an instance of DependencyGraphSubscriptions.
   * @param graphManager - An instance of DependencyGraphManager to interact with.
   */
  constructor(graphManager: DependencyGraphManager) {
    this.graphManager = graphManager;
  }

  getNotifyingUpdateForId(): EntityId | null {
    return this.notifyingUpdateForId;
  }

  /**
   * Subscribe to changes for a specific node.
   * @param params - An object containing id, callback, and key.
   */
  subscribe({ callback, id, key }: SubscriptionParams): void {
    const nodeSubscriptions = this.subscriptions.get(id) ?? new Map<string, () => void>();
    nodeSubscriptions.set(key, callback);
    this.subscriptions.set(id, nodeSubscriptions);
  }

  /**
   * Unsubscribe from changes for a specific node.
   * @param params - An object containing id and key.
   */
  unsubscribe({ id, key }: UnsubscribeParams): void {
    const nodeSubscriptions = this.subscriptions.get(id);
    if (!nodeSubscriptions) return;

    nodeSubscriptions.delete(key);

    // Clean up empty subscription objects
    if (nodeSubscriptions.size === 0) {
      this.subscriptions.delete(id);
    }
  }

  /**
   * Unsubscribe all callbacks for a specific node.
   * @param id - The identifier of the node to remove subscriptions for.
   */
  removeSubscriptions(id: EntityId): void {
    this.subscriptions.delete(id);
  }

  /**
   * Notify all affected nodes of a change.
   * @param update - An object containing the id of the changed node.
   */
  notifyChange(update: DependencyUpdate): void {
    const affected = this.graphManager?.getImpactedNodes(update.id);

    if (!affected || affected.size === 0) {
      return;
    }

    // we are adding set timeout to make sure that all the affected nodes are updated in the next tick,
    // but remember we are not breaking every notification in set timeout, because it causes too much delay
    //
    // Dedupe per id within a tick: if a flush is already scheduled for this id, skip —
    // the pending flush reads current state at run time (subscribers are pull-based), so
    // it already covers this change. `affected` is still captured eagerly on the first
    // call (a node can be deleted before the flush; see the "rapid graph updates" spec).
    if (this.pendingNotifyIds.has(update.id)) {
      return;
    }
    this.pendingNotifyIds.add(update.id);

    setTimeout(() => {
      this.pendingNotifyIds.delete(update.id);
      if (this.notifyingUpdateForId !== null) {
        throw new Error(`Already notifying for ${this.notifyingUpdateForId}`);
      }
      this.notifyingUpdateForId = update.id;

      const affectedArray = Array.from(affected);
      for (let index = 0; index < affectedArray.length; index++) {
        const callbacks = this.subscriptions.get(affectedArray[index]);
        if (callbacks) {
          processCallbacks(callbacks);
        } else {
          // No callbacks found for this node
        }
      }
      this.notifyingUpdateForId = null;
    }, 0);
  }

  /**
   * Notify all subscribed nodes.
   */
  notifyAll(): void {
    const entries = Array.from(this.subscriptions.values());
    for (let i = 0; i < entries.length; i++) {
      processCallbacks(entries[i]);
    }
  }

  /**
   * Reset the subscriptions.
   */
  reset(): void {
    this.subscriptions.clear();
    this.notifyingUpdateForId = null;
    this.pendingNotifyIds.clear();
    this.graphManager = null;
  }
}
